import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CompanyAvatar } from '@/components/ui/company-avatar';
import { cn } from '@/lib/utils';
import type { CompanyBillingData } from '@/types/billing';

interface BillingCompanySelectorProps {
  companies: CompanyBillingData[];
  selectedCompanyId: string | null;
  onSelectCompany: (companyId: string) => void;
  className?: string;
}

export function BillingCompanySelector({
  companies,
  selectedCompanyId,
  onSelectCompany,
  className,
}: BillingCompanySelectorProps) {
  const [open, setOpen] = useState(false);

  const selectedCompany = companies.find(c => c.company.id === selectedCompanyId);

  // Single company - just show display
  if (companies.length === 1) {
    return (
      <div className={cn("flex items-center gap-3 p-4 rounded-lg border bg-card", className)}>
        <CompanyAvatar
          name={companies[0].company.name}
          src={companies[0].company.logo_url}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground">
            {companies[0].company.name}
          </div>
          <div className="text-sm text-muted-foreground">
            {companies[0].tier?.tier_name || 'Free Plan'}
          </div>
        </div>
      </div>
    );
  }

  // Multiple companies - show searchable combobox
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-auto py-3 px-4",
            className
          )}
        >
          {selectedCompany ? (
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <CompanyAvatar
                name={selectedCompany.company.name}
                src={selectedCompany.company.logo_url}
                size="sm"
              />
              <div className="flex-1 min-w-0 text-left">
                <div className="font-semibold text-foreground truncate">
                  {selectedCompany.company.name}
                </div>
                <div className="text-sm text-muted-foreground truncate">
                  {selectedCompany.tier?.tier_name || 'Free Plan'}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">Select company...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search companies..." />
          <CommandList>
            <CommandEmpty>No company found.</CommandEmpty>
            <CommandGroup>
              {companies.map((companyData) => (
                <CommandItem
                  key={companyData.company.id}
                  value={companyData.company.name}
                  onSelect={() => {
                    onSelectCompany(companyData.company.id);
                    setOpen(false);
                  }}
                  className="gap-3 py-3"
                >
                  <CompanyAvatar
                    name={companyData.company.name}
                    src={companyData.company.logo_url}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {companyData.company.name}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {companyData.tier?.tier_name || 'Free Plan'}
                    </div>
                  </div>
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4 shrink-0",
                      selectedCompanyId === companyData.company.id
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
